import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import type {
  FermentationProcedureAlertId,
  FermentationProcedureId,
} from "@brewbase/db/types";
import type { AlertRuleInput } from "@brewbase/lib";
import { trpc } from "@/hooks/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertRulesEditor } from "@/features/fermentation-alerts/alert-rules-editor";

export const Route = createFileRoute(
  "/fermentations/procedures/$procedure_id/alerts",
)({
  component: ProcedureAlertsPage,
  params: z.object({ procedure_id: z.coerce.number() }),
});

/**
 * The alert rules a procedure hands to every run started from it.
 *
 * These are a template and nothing more. A group takes its own copy when the
 * procedure is instantiated, so editing here never reaches a run already under
 * way; the copy on the group is where a live run's alerts are changed. The
 * template follows the procedure's lock like every other part of it.
 */
function ProcedureAlertsPage() {
  const procedureId = Route.useParams().procedure_id as FermentationProcedureId;
  const utils = trpc.useUtils();

  const { data: procedure } =
    trpc.fermentationProcedures.getWithDetails.useQuery(procedureId);
  const { data: rules, isLoading } =
    trpc.fermentationAlerts.listProcedureRules.useQuery(procedureId);

  const invalidate = () =>
    utils.fermentationAlerts.listProcedureRules.invalidate(procedureId);
  const onError = (error: { message: string }) => toast.error(error.message);

  const create = trpc.fermentationAlerts.createProcedureRule.useMutation({
    onSuccess: invalidate,
    onError,
  });
  const update = trpc.fermentationAlerts.updateProcedureRule.useMutation({
    onSuccess: invalidate,
    onError,
  });
  const remove = trpc.fermentationAlerts.deleteProcedureRule.useMutation({
    onSuccess: invalidate,
    onError,
  });

  const locked = procedure?.is_locked ?? false;

  if (isLoading || !rules) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 overflow-y-auto">
      <div>
        <h2 className="text-base font-semibold">Alerts</h2>
        <p className="text-sm text-muted-foreground max-w-3xl">
          What a run started from this procedure watches for, and how long a
          problem must last before the on-call list is called. Every group takes
          its own copy of these rules when it starts, and edits them there, so
          changing anything here affects future runs only.
        </p>
      </div>

      {locked && (
        <p className="text-sm text-muted-foreground max-w-3xl">
          This procedure is locked, so its alert template cannot change. A
          running group's own alerts stay editable on the group.
        </p>
      )}

      <AlertRulesEditor
        rules={rules}
        disabled={locked}
        onCreate={(rule: AlertRuleInput) =>
          create.mutateAsync({ procedureId, rule })
        }
        onUpdate={(id, rule) =>
          update.mutateAsync({ id: id as FermentationProcedureAlertId, rule })
        }
        onDelete={(id) => remove.mutate(id as FermentationProcedureAlertId)}
      />
    </div>
  );
}
